import { InputNumber, InputNumberProps, Typography } from 'antd'
import { SizeType } from 'antd/es/config-provider/SizeContext'
import { AxiosResponse } from 'axios'
import { useGeckoLocale } from 'features/locale'
import isEmpty from 'lodash/isEmpty'
import isEqual from 'lodash/isEqual'
import isNil from 'lodash/isNil'
import lodashSet from 'lodash/set'
import React, {
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import styled from 'styled-components'
import {
  createGeckoAssetModel,
  ForecastComponentConfig,
  GeckoAssetModel,
  GeckoComponentModelJSON,
  GeckoComponentModelType,
  GeckoConeCourseJSON,
  GeckoCourseJSON,
  GeckoFeatureModelType,
  GeckoLegacyCone,
  GeckoMeasurement,
  GeckoMeasurementUnit,
  GeckoVerticalTankShell,
  regenerateAssetModel,
  RolodexEntryPutJson,
} from 'types'
import { assetModelPrecision } from 'utils/constants'
import { getFormattedInstallationDateString } from 'utils/installationDateHelpers'

// A forecast-component-config where a value in `tmin_by_shell_course` or `nominal_by_shell_course` can be null
export type OptionalScForecastComponentConfig = Omit<
  ForecastComponentConfig,
  'tmin_by_shell_course' | 'nominal_by_shell_course' | 'cr_by_shell_course'
> & {
  tmin_by_shell_course?: Record<number, number | null>
  nominal_by_shell_course?: Record<number, number | null>
  cr_by_shell_course?: Record<number, number | null>
}

export function patchSetterDispatch<T, K extends keyof T>(
  setter: Dispatch<SetStateAction<T | null>>,
  key: K
): (value: T[K] | null) => void {
  return (value: T[K] | null) =>
    setter((prev) => {
      return { ...prev, [key]: value ?? undefined } as T
    })
}

type NonUndefined<T> = Exclude<T, undefined>

export function valueOrNull<T, K extends keyof T>(
  source: T | null,
  key: K
): NonUndefined<T[K]> | null {
  const value = source?.[key]
  if (value === undefined) {
    return null
  }
  return value as NonUndefined<T[K]>
}

export function didValueChange<T, K extends keyof T>(
  obj1: T | null,
  obj2: T | null,
  key: K
): boolean {
  return obj1?.[key] !== obj2?.[key] && !isEqual(obj1?.[key], obj2?.[key])
}

export type ParamUpdateTuple<V, S extends string> = {
  [K in S]: V
} & {
  [K in `set${Capitalize<S>}`]: (val: V) => void
} & {
  [K in `${S}Changed`]: boolean
}

export const TabContentContainer = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  overflow-y: scroll;
`

export const INPUT_STYLE = {
  style: { width: '100%' },
  size: 'middle' as SizeType,
}

export const LabeledComponent: FC<{ label: string; children: ReactNode }> = (
  props
) => {
  return (
    <div>
      <Typography.Title level={5}>{props.label}</Typography.Title>
      {props.children}
    </div>
  )
}

type LabeledNumberInputProps = { label: string } & InputNumberProps<number>
export const LabeledNumberInput: FC<LabeledNumberInputProps> = ({
  label,
  ...inputNumberProps
}) => {
  return (
    <LabeledComponent label={label}>
      <InputNumber {...INPUT_STYLE} {...inputNumberProps} />
    </LabeledComponent>
  )
}

export function convertMeasurementUnit(
  value: number | null | undefined,
  fromUnit: GeckoMeasurementUnit,
  toUnit: GeckoMeasurementUnit
) {
  return value !== null && value !== undefined
    ? GeckoMeasurement.CreateFromValue(value, fromUnit).convertTo(toUnit).num
    : null
}

export function getRoundedValue(value: number | null): number | undefined {
  return value ? parseFloat(value.toPrecision(6)) : undefined
}

function capitalizeFirstLetter<S extends string>(str: S): Capitalize<S> {
  if (str.length === 0) return '' as Capitalize<S> // handle empty string
  return (str.charAt(0).toUpperCase() + str.slice(1)) as Capitalize<S>
}

export function useNumberInputParamUpdateProps<K extends string>(
  key: K,
  params: ParamUpdateTuple<number | null, K> | undefined,
  defaultUnit: GeckoMeasurementUnit,
  trackChanged?: (value: number | null) => void
): InputNumberProps<number> {
  const { getPreferredMeasurementUnit } = useGeckoLocale()
  const measUnitId = useMemo(
    () => getPreferredMeasurementUnit(defaultUnit)?.id,
    [defaultUnit, getPreferredMeasurementUnit]
  )

  const setter = params
    ? (params[`set${capitalizeFirstLetter(key)}`] as (
        val: number | null
      ) => void)
    : undefined
  const status = params && params[`${key}Changed`] ? 'warning' : undefined
  const displayVal = useMemo(
    () =>
      params
        ? getRoundedValue(
            convertMeasurementUnit(
              params[key],
              defaultUnit,
              getPreferredMeasurementUnit(defaultUnit)
            )
          )
        : undefined,
    [defaultUnit, getPreferredMeasurementUnit, key, params]
  )
  return useMemo(
    () => ({
      value: displayVal,
      onChange: (value: number | null) => {
        setter?.(
          convertMeasurementUnit(
            value,
            getPreferredMeasurementUnit(defaultUnit),
            defaultUnit
          )
        )
        trackChanged?.(value)
      },
      status,
      addonAfter: measUnitId,
    }),
    [
      displayVal,
      status,
      measUnitId,
      setter,
      getPreferredMeasurementUnit,
      defaultUnit,
      trackChanged,
    ]
  )
}

export function useLabeledNumberInputParamUpdateProps<K extends string>(
  label: string,
  key: K,
  params: ParamUpdateTuple<number | null, K> | undefined,
  defaultUnit: GeckoMeasurementUnit,
  trackChanged?: (value: number | null) => void
): LabeledNumberInputProps {
  const numberInputProps = useNumberInputParamUpdateProps(
    key,
    params,
    defaultUnit,
    trackChanged
  )
  return useMemo(
    () => ({
      ...numberInputProps,
      label,
    }),
    [label, numberInputProps]
  )
}

export const StressToleranceGrid = styled.div`
  width: 100%;
  height: 100%;
  display: grid;
  grid-template-columns: max-content 1fr;
  grid-template-rows: 1fr 1fr;
  justify-content: center;
  align-items: center;
  gap: 0.25rem 0.25rem;
  padding-top: 0.5rem;
`

export enum PpeAssetModelField {
  HEIGHT = 'height',
  DIAMETER = 'diameter',
  NOMINAL = 'nominal',
  INSTALLATION_DATE = 'installation_date',
  TMIN = 'tmin',
}

export enum PpeTankShellAssetModelField {
  SPECIFIC_GRAVITY = 'specific_gravity',
  UPPER_COURSE_STRESS_TOLERANCE = 'stress_tolerance_upper_shell_courses',
  LOWER_COURSE_STRESS_TOLERANCE = 'stress_tolerance_lower_shell_courses',
  FILL_HEIGHT = 'fill_elevation',
  STRESS_TOLERANCE = 'stress_tolerance',
  CROWN_RADIUS = 'crown_radius',
  KNUCKLE_RADIUS = 'knuckle_radius',
  MINOR_RADIUS = 'minor_radius',
}

export enum SharedPipeTankShellAssetModelField {
  JOINT_EFFICIENCY = 'joint_efficiency',
  RADIUS = 'radius',
  DESIGN_PRESSURE = 'design_pressure',
}

export enum PipeSectionAssetModelField {
  ALLOWABLE_STRESS = 'allowable_stress',
}

export enum PipelineAssetModelField {
  OPERATING_PRESSURE = 'operating_pressure',
}

export type ValueUpdater =
  | {
      key: PpeAssetModelField.HEIGHT | PpeAssetModelField.DIAMETER
      value: GeckoMeasurement | undefined
    }
  | {
      key: PpeAssetModelField.NOMINAL
      /** a 1-based index for courses; this will be undefined if the nominal is for the entire component */
      course_index: number | undefined
      value: GeckoMeasurement | undefined
    }
  | {
      key: PpeAssetModelField.INSTALLATION_DATE
      value: Date | undefined
    }
  | {
      key: PpeAssetModelField.TMIN
      /** a 1-based index for courses; this will be undefined if the nominal is for the entire component */
      course_index: number | undefined
      value: GeckoMeasurement | undefined
    }
  | {
      key: SharedPipeTankShellAssetModelField.JOINT_EFFICIENCY
      value: number | undefined
    }
  | {
      key: PpeTankShellAssetModelField.SPECIFIC_GRAVITY
      value: number | undefined
    }
  | {
      key: PpeTankShellAssetModelField.UPPER_COURSE_STRESS_TOLERANCE
      value: GeckoMeasurement | undefined
    }
  | {
      key: PpeTankShellAssetModelField.LOWER_COURSE_STRESS_TOLERANCE
      value: GeckoMeasurement | undefined
    }
  | {
      key: PpeTankShellAssetModelField.FILL_HEIGHT
      value: GeckoMeasurement | undefined
    }
  | {
      key: SharedPipeTankShellAssetModelField.DESIGN_PRESSURE
      value: GeckoMeasurement | undefined
    }
  | {
      key: PpeTankShellAssetModelField.STRESS_TOLERANCE
      value: GeckoMeasurement | undefined
    }
  | {
      key: PpeTankShellAssetModelField.CROWN_RADIUS
      value: GeckoMeasurement | undefined
    }
  | {
      key: PpeTankShellAssetModelField.KNUCKLE_RADIUS
      value: GeckoMeasurement | undefined
    }
  | {
      key: PpeTankShellAssetModelField.MINOR_RADIUS
      value: GeckoMeasurement | undefined
    }
  | {
      key: SharedPipeTankShellAssetModelField.RADIUS
      value: GeckoMeasurement | undefined
    }
  | {
      key: PipeSectionAssetModelField.ALLOWABLE_STRESS
      value: GeckoMeasurement | undefined
    }
  | {
      key: PipelineAssetModelField.OPERATING_PRESSURE
      value: GeckoMeasurement | undefined
    }

/**
 * A hook to get variables/functions to help with updating the RAM values within the PPE
 */
export function useGetAssetModelEditorHelpers(
  baseAssetModel: GeckoAssetModel | undefined,
  componentId: string,
  rolodexPut: (entries: RolodexEntryPutJson[]) => Promise<AxiosResponse>,
  forceRefreshData: () => Promise<void>
) {
  const [userInputAssetModel, setUserInputAssetModel] = useState<
    GeckoAssetModel | undefined
  >(baseAssetModel)
  const userInputComponentModel =
    userInputAssetModel?.data.components.get(componentId)

  /** Set the defaults for the `userInputAssetModel` from the `assetModel` values (when loaded in) */
  useEffect(() => {
    // If the assetModel is still loading, or if the userInputAssetModel has been set, do nothing
    setUserInputAssetModel(baseAssetModel)
  }, [baseAssetModel, setUserInputAssetModel])

  /** A function to update values in the userInputAssetModel */
  const updateAssetModelVals = useCallback(
    (params: ValueUpdater) => {
      if (isNil(userInputAssetModel) || isNil(userInputComponentModel)) {
        throw new Error(
          'userInputAssetModel or userInputComponentModel is null'
        )
      }

      const newAssetModelJson = userInputAssetModel.raw
      // this variable will be mutated in-place in the switch statement
      //    We're mutating in-place so that we can re-use the newAssetModelJson object to generate a new GeckoAssetModel
      const thisComponentDataJson: GeckoComponentModelJSON | undefined = (
        newAssetModelJson.data['components'] as Record<
          string,
          GeckoComponentModelJSON
        >
      )[componentId]

      if (isNil(thisComponentDataJson)) {
        throw new Error(
          `No component was found for the provided id: ${componentId}, ${newAssetModelJson.data['components']}`
        )
      }

      // edit `thisComponentDataJson` in place
      switch (params.key) {
        case PpeAssetModelField.NOMINAL: {
          editDataNominals(params, componentId, thisComponentDataJson)
          break
        }
        case PpeAssetModelField.TMIN: {
          //TODO: Currently not surppoting fixed tmins for pipes and horizontal tanks need to fix this
          editDataTmins(params, componentId, thisComponentDataJson)
          break
        }
        case PpeAssetModelField.DIAMETER: {
          const radius = params.value
            ?.scale(0.5)
            .convertTo(GeckoMeasurementUnit.Meters)
            .toPrecision(assetModelPrecision).num

          lodashSet(thisComponentDataJson, 'radius', radius)
          break
        }
        case PpeAssetModelField.HEIGHT: {
          const height = params.value
            ?.convertTo(GeckoMeasurementUnit.Meters)
            .toPrecision(assetModelPrecision).num

          lodashSet(thisComponentDataJson, 'height', height)
          break
        }
        case PpeAssetModelField.INSTALLATION_DATE: {
          newAssetModelJson.data[PpeAssetModelField.INSTALLATION_DATE] =
            !params.value
              ? undefined
              : getFormattedInstallationDateString(params.value)
          break
        }
        case SharedPipeTankShellAssetModelField.JOINT_EFFICIENCY: {
          lodashSet(
            thisComponentDataJson,
            SharedPipeTankShellAssetModelField.JOINT_EFFICIENCY,
            params.value ? params.value : undefined
          )
          break
        }
        case PpeTankShellAssetModelField.SPECIFIC_GRAVITY: {
          newAssetModelJson.data[PpeTankShellAssetModelField.SPECIFIC_GRAVITY] =
            params.value ? params.value : undefined
          break
        }
        case PpeTankShellAssetModelField.FILL_HEIGHT: {
          const fillHeight = params.value
            ?.convertTo(GeckoMeasurementUnit.Meters)
            .toPrecision(assetModelPrecision).num

          newAssetModelJson.data[PpeTankShellAssetModelField.FILL_HEIGHT] =
            fillHeight
          break
        }
        case PpeTankShellAssetModelField.UPPER_COURSE_STRESS_TOLERANCE: {
          //for now add the lower course tolerances to both the top level and the bottom two courses
          editStressTolerances(
            {
              key: PpeTankShellAssetModelField.UPPER_COURSE_STRESS_TOLERANCE,
              value: params.value,
            },
            componentId,
            thisComponentDataJson
          )

          break
        }
        case PpeTankShellAssetModelField.LOWER_COURSE_STRESS_TOLERANCE: {
          //for now add the upper course tolerances to both the top level and the all courses above the second
          editStressTolerances(
            {
              key: PpeTankShellAssetModelField.LOWER_COURSE_STRESS_TOLERANCE,
              value: params.value,
            },
            componentId,
            thisComponentDataJson
          )
          break
        }
        case SharedPipeTankShellAssetModelField.DESIGN_PRESSURE: {
          lodashSet(
            thisComponentDataJson,
            SharedPipeTankShellAssetModelField.DESIGN_PRESSURE,
            params.value
              ?.convertTo(GeckoMeasurementUnit.Megapascals)
              .toPrecision(assetModelPrecision).num
          )
          break
        }
        case PpeTankShellAssetModelField.STRESS_TOLERANCE: {
          lodashSet(
            thisComponentDataJson,
            PpeTankShellAssetModelField.STRESS_TOLERANCE,
            params.value
              ?.convertTo(GeckoMeasurementUnit.Megapascals)
              .toPrecision(assetModelPrecision).num
          )
          break
        }
        case PpeTankShellAssetModelField.CROWN_RADIUS: {
          lodashSet(
            thisComponentDataJson,
            PpeTankShellAssetModelField.CROWN_RADIUS,
            params.value
              ?.convertTo(GeckoMeasurementUnit.Millimeters)
              .toPrecision(assetModelPrecision).num
          )
          break
        }
        case PpeTankShellAssetModelField.KNUCKLE_RADIUS: {
          lodashSet(
            thisComponentDataJson,
            PpeTankShellAssetModelField.KNUCKLE_RADIUS,
            params.value
              ?.convertTo(GeckoMeasurementUnit.Millimeters)
              .toPrecision(assetModelPrecision).num
          )
          break
        }
        case PpeTankShellAssetModelField.MINOR_RADIUS: {
          lodashSet(
            thisComponentDataJson,
            PpeTankShellAssetModelField.MINOR_RADIUS,
            params.value
              ?.convertTo(GeckoMeasurementUnit.Millimeters)
              .toPrecision(assetModelPrecision).num
          )
          break
        }
        case SharedPipeTankShellAssetModelField.RADIUS: {
          lodashSet(
            thisComponentDataJson,
            SharedPipeTankShellAssetModelField.RADIUS,
            params.value
              ?.convertTo(GeckoMeasurementUnit.Meters)
              .toPrecision(assetModelPrecision).num
          )
          break
        }
        case PipeSectionAssetModelField.ALLOWABLE_STRESS: {
          lodashSet(
            thisComponentDataJson,
            PipeSectionAssetModelField.ALLOWABLE_STRESS,
            params.value
              ?.convertTo(GeckoMeasurementUnit.Megapascals)
              .toPrecision(assetModelPrecision).num
          )
          break
        }
        case PipelineAssetModelField.OPERATING_PRESSURE: {
          newAssetModelJson.data[PipelineAssetModelField.OPERATING_PRESSURE] =
            params.value
              ?.convertTo(GeckoMeasurementUnit.Megapascals)
              .toPrecision(assetModelPrecision).num
          break
        }
      }

      setUserInputAssetModel(createGeckoAssetModel(newAssetModelJson))
    },
    [
      componentId,
      userInputAssetModel,
      userInputComponentModel,
      setUserInputAssetModel,
    ]
  )

  const hasAssetModelChanged = useMemo(() => {
    return (
      isEmpty(userInputAssetModel) !== isEmpty(baseAssetModel) ||
      !isEqual(userInputAssetModel, baseAssetModel)
    )
  }, [baseAssetModel, userInputAssetModel])

  const resetUserInputRamData = useCallback(() => {
    setUserInputAssetModel(baseAssetModel)
  }, [setUserInputAssetModel, baseAssetModel])

  const [isSavingAssetModel, setIsSavingAssetModel] = useState(false)

  const saveAssetModelToRolodex = useCallback(async () => {
    if (!userInputAssetModel) {
      throw new Error('tried to save a non-existent asset-model')
    }
    setIsSavingAssetModel(true)
    const putBody = regenerateAssetModel(userInputAssetModel)
    const res = await rolodexPut([putBody])
    if (res.status >= 300) {
      alert('Failed to save asset model')
    }
    await forceRefreshData()
    setIsSavingAssetModel(false)
  }, [userInputAssetModel, rolodexPut, forceRefreshData])

  return {
    userInputAssetModel,
    updateAssetModelVals,
    hasAssetModelChanged,
    resetUserInputRamData,
    saveAssetModelToRolodex,
    isSavingAssetModel,
  }
}

/**
 * Handle updating the RAM stress tolerances for vertical Tank Shells.
 *
 * NOTE: this edits `thisComponentDataJson` in place
 */
function editStressTolerances(
  params: {
    key:
      | PpeTankShellAssetModelField.UPPER_COURSE_STRESS_TOLERANCE
      | PpeTankShellAssetModelField.LOWER_COURSE_STRESS_TOLERANCE
    value: GeckoMeasurement | undefined
  },
  componentId: string,
  thisComponentDataJson: GeckoComponentModelJSON
): void {
  if (
    thisComponentDataJson.type === GeckoComponentModelType.VerticalTankShell
  ) {
    const componentModel: GeckoVerticalTankShell =
      GeckoAssetModel.generateComponent(
        componentId,
        thisComponentDataJson
      ) as GeckoVerticalTankShell

    // handle shell-courses
    const sortedShellCourses = componentModel.coursesArray.toSorted(
      (a, b) => a.geometry.y.end.minus(b.geometry.y.end).num
    )
    let shellCourseIds: string[] = []
    switch (params.key) {
      case PpeTankShellAssetModelField.LOWER_COURSE_STRESS_TOLERANCE: {
        // set the stress tolerance for all courses below the second course
        shellCourseIds = sortedShellCourses
          .slice(0, 2)
          .map((course) => course.id)
        thisComponentDataJson.stress_tolerance_lower_shell_courses =
          params.value
            ?.convertTo(GeckoMeasurementUnit.Megapascals)
            .toPrecision(assetModelPrecision).num
        break
      }
      case PpeTankShellAssetModelField.UPPER_COURSE_STRESS_TOLERANCE: {
        // set the stress tolerance for all courses above the second course
        //no sense in checking if the length of shell courses is greater than 2 since iteration won't throw errors anyways
        shellCourseIds = sortedShellCourses.slice(2).map((course) => course.id)
        thisComponentDataJson.stress_tolerance_upper_shell_courses =
          params.value
            ?.convertTo(GeckoMeasurementUnit.Megapascals)
            .toPrecision(assetModelPrecision).num
        break
      }
    }

    shellCourseIds.forEach((courseId) => {
      const assetModelCourse = thisComponentDataJson.features[courseId] as
        | GeckoCourseJSON
        | undefined

      if (isNil(assetModelCourse) || assetModelCourse.type !== 'course') {
        throw new Error(
          `No shell course was found for the provided id: ${courseId}, ${thisComponentDataJson.features}`
        )
      }
      assetModelCourse.stress_tolerance_override = params.value
        ?.convertTo(GeckoMeasurementUnit.Megapascals)
        .toPrecision(assetModelPrecision).num
    })
  }
}

/**
 * Handle updating the RAM nominals for the different component types.
 *
 * NOTE: this edits `thisComponentDataJson` in place
 */
function editDataNominals(
  params: {
    key: PpeAssetModelField.NOMINAL
    course_index: number | undefined
    value: GeckoMeasurement | undefined
  },
  componentId: string,
  thisComponentDataJson: GeckoComponentModelJSON
): void {
  // Nominal is more tricky because of shell-courses and walls
  if (
    thisComponentDataJson.type === GeckoComponentModelType.VerticalTankShell
  ) {
    if (isNil(params.course_index)) {
      throw new Error(
        'A shell course number was not provided for a nominal on a component with shell-courses'
      )
    }
    const componentModel: GeckoVerticalTankShell =
      GeckoAssetModel.generateComponent(
        componentId,
        thisComponentDataJson
      ) as GeckoVerticalTankShell
    // handle shell-courses
    const sortedShellCourses = componentModel.coursesArray.toSorted(
      (a, b) => a.geometry.y.end.minus(b.geometry.y.end).num
    )

    const thisShellCourseId = sortedShellCourses.at(
      // we do `-1` because the index is 1-based in the params (this is because of how they were stored in the FCC)
      params.course_index - 1
    )?.id
    if (isNil(thisShellCourseId)) {
      throw new Error(
        `No shell course was found for the provided index: ${params.course_index}, ${sortedShellCourses}`
      )
    }

    const thisCourseJson = thisComponentDataJson.features[thisShellCourseId] as
      | GeckoCourseJSON
      | undefined

    if (isNil(thisCourseJson)) {
      throw new Error(
        `No shell course was found for the provided id: ${thisShellCourseId}, ${thisComponentDataJson.features}`
      )
    }

    thisCourseJson['nominal']['wall_thickness'] = params.value
      ?.convertTo(GeckoMeasurementUnit.Meters)
      .toPrecision(assetModelPrecision).num
  } else if (
    thisComponentDataJson.type === GeckoComponentModelType.LegacyCone
  ) {
    if (isNil(params.course_index)) {
      throw new Error(
        'A shell course number was not provided for a nominal on a component with shell-courses'
      )
    }
    const componentModel: GeckoLegacyCone = GeckoAssetModel.generateComponent(
      componentId,
      thisComponentDataJson
    ) as GeckoLegacyCone
    // handle shell-courses
    const sortedCourses = componentModel.coursesArray.toSorted(
      (a, b) => a.geometry.y.end.minus(b.geometry.y.end).num
    )

    const thisShellCourseId = sortedCourses.at(
      // we do `-1` because the index is 1-based in the params (this is because of how they were stored in the FCC)
      params.course_index - 1
    )?.id
    if (isNil(thisShellCourseId)) {
      throw new Error(
        `No shell course was found for the provided index: ${params.course_index}, ${sortedCourses}`
      )
    }

    const thisCourseJson = thisComponentDataJson.features[thisShellCourseId] as
      | GeckoConeCourseJSON
      | undefined

    if (isNil(thisCourseJson)) {
      throw new Error(
        `No shell course was found for the provided id: ${thisShellCourseId}, ${thisComponentDataJson.features}`
      )
    }

    thisCourseJson['nominal']['wall_thickness'] = params.value
      ?.convertTo(GeckoMeasurementUnit.Meters)
      .toPrecision(assetModelPrecision).num
  } else if (
    thisComponentDataJson.type === GeckoComponentModelType.LegacyBoilerWalls
  ) {
    // handle boiler-wall nominals
    Object.values(thisComponentDataJson['features'] ?? {}).forEach(
      (feature) => {
        if (feature.type !== GeckoFeatureModelType.LegacyBoilerWall) {
          return
        }
        feature['nominal']['wall_thickness'] = params.value
          ?.convertTo(GeckoMeasurementUnit.Meters)
          .toPrecision(assetModelPrecision).num
      }
    )
  } else if (
    thisComponentDataJson.type === GeckoComponentModelType.ShipDeck ||
    thisComponentDataJson.type === GeckoComponentModelType.BasicShipHull
  ) {
    return undefined
  } else if (
    thisComponentDataJson.type === GeckoComponentModelType.TankFloor ||
    thisComponentDataJson.type === GeckoComponentModelType.TankFlatRoof
  ) {
    // handle global nominals
    thisComponentDataJson['nominal']['base_thickness'] = params.value
      ?.convertTo(GeckoMeasurementUnit.Meters)
      .toPrecision(assetModelPrecision).num
  } else if (
    thisComponentDataJson.type ===
      GeckoComponentModelType.HorizontalTankShell ||
    thisComponentDataJson.type === GeckoComponentModelType.PipeSection
  ) {
    thisComponentDataJson['nominal']['wall_thickness'] = params.value
      ?.convertTo(GeckoMeasurementUnit.Meters)
      .toPrecision(assetModelPrecision).num
  }
}

/**
 * Handle updating the RAM Tmin for the different component types.
 *
 * NOTE: this edits `thisComponentDataJson` in place
 */
function editDataTmins(
  params: {
    key: PpeAssetModelField.TMIN
    course_index: number | undefined
    value: GeckoMeasurement | undefined
  },
  componentId: string,
  thisComponentDataJson: GeckoComponentModelJSON
): void {
  // Nominal is more tricky because of shell-courses and walls
  if (
    thisComponentDataJson.type === GeckoComponentModelType.VerticalTankShell
  ) {
    if (isNil(params.course_index)) {
      throw new Error(
        'A shell course number was not provided for a nominal on a component with shell-courses'
      )
    }
    const componentModel: GeckoVerticalTankShell =
      GeckoAssetModel.generateComponent(
        componentId,
        thisComponentDataJson
      ) as GeckoVerticalTankShell
    // handle shell-courses
    const sortedShellCourses = componentModel.coursesArray.toSorted(
      (a, b) => a.geometry.y.end.minus(b.geometry.y.end).num
    )

    const thisShellCourseId = sortedShellCourses.at(
      // we do `-1` because the index is 1-based in the params (this is because of how they were stored in the FCC)
      params.course_index - 1
    )?.id
    if (isNil(thisShellCourseId)) {
      throw new Error(
        `No shell course was found for the provided index: ${params.course_index}, ${sortedShellCourses}`
      )
    }

    const thisCourseJson = thisComponentDataJson.features[thisShellCourseId] as
      | GeckoCourseJSON
      | undefined

    if (isNil(thisCourseJson)) {
      throw new Error(
        `No shell course was found for the provided id: ${thisShellCourseId}, ${thisComponentDataJson.features}`
      )
    }

    thisCourseJson['tmin'] = params.value
      ?.convertTo(GeckoMeasurementUnit.Meters)
      .toPrecision(assetModelPrecision).num
  } else if (
    thisComponentDataJson.type === GeckoComponentModelType.LegacyCone
  ) {
    if (isNil(params.course_index)) {
      throw new Error(
        'A shell course number was not provided for a nominal on a component with shell-courses'
      )
    }
    const componentModel: GeckoLegacyCone = GeckoAssetModel.generateComponent(
      componentId,
      thisComponentDataJson
    ) as GeckoLegacyCone
    // handle shell-courses
    const sortedCourses = componentModel.coursesArray.toSorted(
      (a, b) => a.geometry.y.end.minus(b.geometry.y.end).num
    )

    const thisShellCourseId = sortedCourses.at(
      // we do `-1` because the index is 1-based in the params (this is because of how they were stored in the FCC)
      params.course_index - 1
    )?.id
    if (isNil(thisShellCourseId)) {
      throw new Error(
        `No shell course was found for the provided index: ${params.course_index}, ${sortedCourses}`
      )
    }

    const thisCourseJson = thisComponentDataJson.features[thisShellCourseId] as
      | GeckoConeCourseJSON
      | undefined

    if (isNil(thisCourseJson)) {
      throw new Error(
        `No shell course was found for the provided id: ${thisShellCourseId}, ${thisComponentDataJson.features}`
      )
    }

    thisCourseJson['tmin'] = params.value
      ?.convertTo(GeckoMeasurementUnit.Meters)
      .toPrecision(assetModelPrecision).num
  } else if (
    thisComponentDataJson.type === GeckoComponentModelType.LegacyBoilerWalls
  ) {
    // handle boiler-wall nominals
    Object.values(thisComponentDataJson['features'] ?? {}).forEach(
      (feature) => {
        if (feature.type !== GeckoFeatureModelType.LegacyBoilerWall) {
          return
        }
        feature['tmin'] = params.value
          ?.convertTo(GeckoMeasurementUnit.Meters)
          .toPrecision(assetModelPrecision).num
      }
    )
  } else if (
    thisComponentDataJson.type === GeckoComponentModelType.ShipDeck ||
    thisComponentDataJson.type === GeckoComponentModelType.BasicShipHull
  ) {
    // For ship decks, we always compute T-min based on a percentage of the nominal,
    // which is currently set to 25% corrosion which is 75% of the nominal
    return undefined
  } else if (
    thisComponentDataJson.type === GeckoComponentModelType.TankFloor ||
    thisComponentDataJson.type === GeckoComponentModelType.TankFlatRoof ||
    thisComponentDataJson.type ===
      GeckoComponentModelType.HorizontalTankShell ||
    thisComponentDataJson.type === GeckoComponentModelType.PipeSection ||
    thisComponentDataJson.type ===
      GeckoComponentModelType.PressureVesselShell ||
    thisComponentDataJson.type === GeckoComponentModelType.HemisphericalHead ||
    thisComponentDataJson.type === GeckoComponentModelType.TorisphericalHead ||
    thisComponentDataJson.type === GeckoComponentModelType.EllipsoidalHead
  ) {
    // handle global nominals
    thisComponentDataJson['tmin'] = params.value
      ?.convertTo(GeckoMeasurementUnit.Meters)
      .toPrecision(assetModelPrecision).num
  }
}
